import { useEffect, useState } from 'preact/hooks';
import { Link, useLocation, useParams, useSearch } from 'wouter-preact';
import { api, ApiErr, type AssessmentRow, type ClassRow, type ItemRow, type CodeRow, type Dataset } from '@/lib/teaching/teacher-api';
import { refreshAssessments, invalidateDataset, teachHref } from '@/lib/teaching/state';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { Field, Btn, DownloadSpreadsheet, ErrorLine, inputCls, val } from '@/components/teaching/form';

const STAGE: Record<AssessmentRow['stage'], { label: string; bg: string }> = {
  setup: { label: 'Setting up', bg: '#e2e3e6' },
  marking: { label: 'Marking', bg: '#fde9b8' },
  analysed: { label: 'Analysed', bg: '#d3f0d9' },
};

export function Tests() {
  const [list, setList] = useState<AssessmentRow[] | null>(null);
  useEffect(() => { void refreshAssessments().then(setList); }, []);
  return (
    <TeachingPage title="Tests" breadcrumb="Every test you have set" actions={<Link href="/setup/tests/new" class="h-9 px-3 rounded-lg bg-[var(--color-accent)] text-[13px] font-medium grid place-items-center" style={{ color: '#ffffff' }}>New test</Link>}>
      <div class="flex flex-col gap-2">
        {list?.map((a) => (
          <div key={a.id} class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-4 flex flex-wrap items-center gap-3">
            <div class="min-w-0 flex-1">
              <div class="text-[15px] font-semibold text-[var(--color-text)]">{a.title}</div>
              <div class="text-[12px] text-[var(--color-text-muted)]">Year {a.year_level} · Criterion {a.criterion}{a.sat_on ? ` · ${a.sat_on}` : ''} · {a.item_count} questions · {a.student_count} students</div>
            </div>
            <span class="px-2 py-0.5 rounded text-[11px] font-medium" style={{ background: STAGE[a.stage].bg, color: '#1a1a1a' }}>{STAGE[a.stage].label}</span>
            <Link href={`/setup/tests/${a.id}`} class="text-[12.5px] text-[var(--color-accent)]">Edit</Link>
            <Link href={teachHref('/setup/marks', { a: a.id })} class="text-[12.5px] text-[var(--color-accent)]">Marks</Link>
            <Link href={teachHref('/teaching', { a: a.id })} class="text-[12.5px] text-[var(--color-accent)]">Analysis</Link>
          </div>
        ))}
        {list && list.length === 0 && <div class="text-[13px] text-[var(--color-text-muted)]">No tests yet.</div>}
      </div>
    </TeachingPage>
  );
}

// ── Wizard: details → classes → questions → misconception codes ───────

const STEPS = ['Details', 'Classes', 'Questions', 'Codes'] as const;

export function TestWizard() {
  const params = useParams<{ id?: string }>();
  const search = useSearch();
  const [, navigate] = useLocation();
  const id = params.id && params.id !== 'new' ? params.id : null;
  const step = Math.min(3, Math.max(0, Number(new URLSearchParams(search).get('step') ?? 0)));
  const go = (s: number, aid = id) => navigate(`/setup/tests/${aid ?? 'new'}?step=${s}`);

  const [ds, setDs] = useState<Dataset | null>(null);
  const [classes, setClasses] = useState<ClassRow[]>([]);
  const reload = async () => {
    setClasses(await api<ClassRow[]>('GET', '/api/t/classes'));
    if (id) setDs(await api<Dataset>('GET', `/api/t/assessments/${id}/dataset`));
  };
  useEffect(() => { void reload(); }, [id]);
  const after = async () => { if (id) invalidateDataset(id); await reload(); void refreshAssessments(); };

  return (
    <TeachingPage title={id ? (ds?.assessment.title ?? 'Test') : 'New test'} breadcrumb="Tests"
      actions={<>{id && <DownloadSpreadsheet a={id} />}<Link href="/setup/tests" class="text-[12.5px] text-[var(--color-accent)]">All tests</Link></>}>
      <nav class="flex flex-wrap gap-1">
        {STEPS.map((s, i) => (
          <button key={s} type="button" disabled={!id && i > 0} onClick={() => go(i)}
            class={'px-3 py-1.5 rounded-lg text-[12.5px] font-medium border ' + (i === step ? 'bg-[var(--color-accent)] border-transparent' : 'border-[var(--color-border)] text-[var(--color-text-muted)] disabled:opacity-40')}
            style={i === step ? { color: '#ffffff' } : undefined}>{i + 1}. {s}</button>
        ))}
      </nav>
      {ds?.assessment.marking_started_at && (
        <div class="rounded-xl border border-[var(--color-border)] bg-[var(--color-elevated)] px-3 py-2 text-[12.5px] text-[var(--color-text-muted)]">
          Marking has started. Question text can still be edited; anything else goes through "Change after marking", which is logged.
        </div>
      )}
      {step === 0 && <DetailsStep ds={ds} onSaved={(aid) => { void after(); go(1, aid); }} />}
      {step === 1 && ds && <ClassesStep ds={ds} classes={classes} onSaved={() => { void after(); go(2); }} />}
      {step === 2 && ds && <QuestionsStep ds={ds} onSaved={() => { void after(); go(3); }} onChanged={after} />}
      {step === 3 && ds && <CodesStep ds={ds} onSaved={async () => { await after(); navigate(teachHref('/setup/marks', { a: ds.assessment.id })); }} />}
    </TeachingPage>
  );
}

function DetailsStep({ ds, onSaved }: { ds: Dataset | null; onSaved: (id: string) => void }) {
  const a = ds?.assessment;
  const [title, setTitle] = useState(a?.title ?? '');
  const [year, setYear] = useState(String(a?.year_level ?? 7));
  const [crit, setCrit] = useState(a?.criterion ?? 'A');
  const [date, setDate] = useState(a?.sat_on ?? '');
  const [err, setErr] = useState<ApiErr | null>(null);
  useEffect(() => { if (a) { setTitle(a.title); setYear(String(a.year_level)); setCrit(a.criterion); setDate(a.sat_on ?? ''); } }, [a?.id]);

  async function save(e: Event) {
    e.preventDefault(); setErr(null);
    const body = { title, year_level: Number(year), criterion: crit, sat_on: date || null };
    try {
      const r = a ? await api<AssessmentRow>('PATCH', `/api/t/assessments/${a.id}`, body, a.revision)
        : await api<AssessmentRow>('POST', '/api/t/assessments', body);
      onSaved(r.id);
    } catch (x) { setErr(x as ApiErr); }
  }
  return (
    <Card title="Details">
      <form onSubmit={save} class="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <Field label="Title" error={err?.fields?.title} class="sm:col-span-2"><input class={inputCls} value={title} onInput={(e) => setTitle(val(e))} required placeholder="Forces and motion" /></Field>
        <Field label="Year level" error={err?.fields?.year_level}><input class={inputCls} type="number" min={1} max={13} value={year} onInput={(e) => setYear(val(e))} /></Field>
        <Field label="Criterion" error={err?.fields?.criterion}>
          <select class={inputCls} value={crit} onChange={(e) => setCrit(val(e))}>{['A', 'B', 'C', 'D'].map((c) => <option key={c}>{c}</option>)}</select>
        </Field>
        <Field label="Date sat" error={err?.fields?.sat_on}><input class={inputCls} type="date" value={date} onInput={(e) => setDate(val(e))} /></Field>
        <div class="sm:col-span-2 lg:col-span-3 flex justify-end"><Btn kind="primary" type="submit">{a ? 'Save and continue' : 'Create and continue'}</Btn></div>
      </form>
      <ErrorLine error={err && !err.fields ? err.error : null} />
    </Card>
  );
}

function ClassesStep({ ds, classes, onSaved }: { ds: Dataset; classes: ClassRow[]; onSaved: () => void }) {
  const [pick, setPick] = useState<Set<string>>(new Set(ds.assessment.class_ids ?? []));
  // V9, V25: a class may have sat on its own date; blank uses the test date.
  const [dates, setDates] = useState<Record<string, string>>(Object.fromEntries(Object.entries(ds.assessment.class_dates ?? {}).map(([k, v]) => [k, v ?? ''])));
  const [err, setErr] = useState<ApiErr | null>(null);
  const live = classes.filter((c) => !c.archived || pick.has(c.id));
  async function save() {
    setErr(null);
    try {
      const saved = await api<AssessmentRow>('PATCH', `/api/t/assessments/${ds.assessment.id}`, { class_ids: [...pick] }, ds.assessment.revision);
      const class_dates = Object.fromEntries([...pick].map((c) => [c, dates[c] || null]));
      await api('PATCH', `/api/t/assessments/${ds.assessment.id}`, { class_dates }, saved.revision);
      onSaved();
    }
    catch (x) { setErr(x as ApiErr); }
  }
  return (
    <Card title="Which classes sat it">
      {live.length === 0 && <div class="text-[13px] text-[var(--color-text-muted)]">No classes yet. <Link href="/setup/classes" class="text-[var(--color-accent)]">Add one first</Link>.</div>}
      <div class="flex flex-wrap gap-2">
        {live.map((c) => (
          <label key={c.id} class={'flex items-center gap-2 px-3 py-2 rounded-xl border text-[13px] cursor-pointer ' + (pick.has(c.id) ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)]' : 'border-[var(--color-border)]')}>
            <input type="checkbox" checked={pick.has(c.id)} onChange={() => { const n = new Set(pick); n.has(c.id) ? n.delete(c.id) : n.add(c.id); setPick(n); }} />
            <span class="text-[var(--color-text)]">{c.name}</span>
            <span class="text-[11px] text-[var(--color-text-faint)]">{c.student_count}</span>
            {pick.has(c.id) && (
              <input aria-label={`Date ${c.name} sat`} type="date" class={inputCls + ' h-7 text-[12px]'} value={dates[c.id] ?? ''}
                title="Leave blank to use the test date" onInput={(e) => setDates({ ...dates, [c.id]: val(e) })} />
            )}
          </label>
        ))}
      </div>
      <ErrorLine error={err?.error} />
      <div class="flex justify-end mt-3"><Btn kind="primary" onClick={save} disabled={pick.size === 0}>Save and continue</Btn></div>
    </Card>
  );
}

type Row = Omit<ItemRow, 'id' | 'position'> & { id?: string };
const blankRow = (n: number): Row => ({ label: `Q${n}`, prompt: '', max_mark: 1, threshold: 1, topic: '', strand: '', command_term: '' });

function QuestionsStep({ ds, onSaved, onChanged }: { ds: Dataset; onSaved: () => void; onChanged: () => Promise<void> }) {
  const locked = !!ds.assessment.marking_started_at;
  const [rows, setRows] = useState<Row[]>(ds.items.length ? ds.items.map(({ position: _, ...r }) => r) : [blankRow(1)]);
  const [ext, setExt] = useState<string>(ds.assessment.extension_item_id ?? '');
  const [err, setErr] = useState<ApiErr | null>(null);
  useEffect(() => { if (ds.items.length) setRows(ds.items.map(({ position: _, ...r }) => r)); }, [ds.assessment.revision]);
  const set = (i: number, k: keyof Row, v: string) => setRows(rows.map((r, j) => j === i ? { ...r, [k]: k === 'max_mark' || k === 'threshold' ? Number(v) : v } : r));
  const fe = (i: number, k: string) => err?.fields?.[`items.${i}.${k}`];

  async function save() {
    setErr(null);
    try {
      const items = await api<ItemRow[]>('PUT', `/api/t/assessments/${ds.assessment.id}/items`, { items: rows }, ds.assessment.revision);
      // Default extension question: the highest-max one (spec R10).
      const extId = ext && items.some((x) => x.id === ext) ? ext : [...items].sort((a, b) => b.max_mark - a.max_mark)[0]?.id;
      const cur = await api<AssessmentRow>('GET', `/api/t/assessments/${ds.assessment.id}`);
      if (extId && extId !== cur.extension_item_id) await api('PATCH', `/api/t/assessments/${ds.assessment.id}`, { extension_item_id: extId }, cur.revision);
      onSaved();
    } catch (x) { setErr(x as ApiErr); }
  }

  async function correct(item: ItemRow) {
    const reason = prompt(`Change after marking: ${item.label}\nWhy? (this is logged)`);
    if (!reason) return;
    const max = prompt('Max mark', String(item.max_mark)); if (max === null) return;
    const thr = prompt('Threshold (mastery bar)', String(item.threshold)); if (thr === null) return;
    const topic = prompt('Topic', item.topic); if (topic === null) return;
    setErr(null);
    const fresh = await api<Dataset>('GET', `/api/t/assessments/${ds.assessment.id}/dataset`);
    const rowRevs = Object.fromEntries(fresh.students.map((s) => [s.student_id, s.revision]));
    try {
      await api('POST', `/api/t/assessments/${ds.assessment.id}/items/${item.id}/correction`, {
        reason, expectedAssessmentRevision: fresh.assessment.revision, expectedRowRevisions: rowRevs,
        changes: { max_mark: Number(max), threshold: Number(thr), topic },
      });
      await onChanged();
    } catch (x) { setErr(x as ApiErr); }
  }

  return (
    <Card title="Questions" sub={locked ? 'Locked: edit question text here, use Change for anything else' : 'Thresholds are the mastery bar, set before any results (SOP §31)'}>
      <div class="overflow-x-auto -mx-1">
        <table class="w-full text-[12.5px] min-w-[760px]">
          <thead><tr class="text-left text-[11px] uppercase tracking-wider text-[var(--color-text-faint)]">
            <th class="p-1 w-16">Label</th><th class="p-1">Question</th><th class="p-1 w-16">Max</th><th class="p-1 w-20">Threshold</th>
            <th class="p-1 w-36">Topic</th><th class="p-1 w-20">Strand</th><th class="p-1 w-28">Command</th><th class="p-1 w-16">Extension</th><th class="p-1 w-16" />
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.id ?? `new${i}`} class="align-top">
                <td class="p-1"><input aria-label={`Label ${i + 1}`} class={inputCls + ' w-full'} value={r.label} disabled={locked} onInput={(e) => set(i, 'label', val(e))} />{fe(i, 'label') && <div class="text-[11px] text-[#b42318]">{fe(i, 'label')}</div>}</td>
                <td class="p-1"><input aria-label={`Question ${i + 1}`} class={inputCls + ' w-full'} value={r.prompt} onInput={(e) => set(i, 'prompt', val(e))} /></td>
                <td class="p-1"><input aria-label={`Max ${i + 1}`} class={inputCls + ' w-full'} type="number" min={1} value={r.max_mark} disabled={locked} onInput={(e) => set(i, 'max_mark', val(e))} />{fe(i, 'max_mark') && <div class="text-[11px] text-[#b42318]">{fe(i, 'max_mark')}</div>}</td>
                <td class="p-1"><input aria-label={`Threshold ${i + 1}`} class={inputCls + ' w-full'} type="number" min={1} value={r.threshold} disabled={locked} onInput={(e) => set(i, 'threshold', val(e))} />{fe(i, 'threshold') && <div class="text-[11px] text-[#b42318]">{fe(i, 'threshold')}</div>}</td>
                <td class="p-1"><input aria-label={`Topic ${i + 1}`} class={inputCls + ' w-full'} value={r.topic} disabled={locked} onInput={(e) => set(i, 'topic', val(e))} /></td>
                <td class="p-1"><input aria-label={`Strand ${i + 1}`} class={inputCls + ' w-full'} value={r.strand} disabled={locked} placeholder="A(i)" onInput={(e) => set(i, 'strand', val(e))} /></td>
                <td class="p-1"><input aria-label={`Command ${i + 1}`} class={inputCls + ' w-full'} value={r.command_term} disabled={locked} placeholder="explain" onInput={(e) => set(i, 'command_term', val(e))} /></td>
                <td class="p-1 text-center"><input type="radio" name="ext" aria-label={`Extension ${i + 1}`} checked={!!r.id && ext === r.id} disabled={!r.id || locked} onChange={() => setExt(r.id!)} /></td>
                <td class="p-1">
                  {locked && r.id
                    ? <Btn onClick={() => correct(ds.items.find((x) => x.id === r.id)!)}>Change</Btn>
                    : <Btn kind="danger" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}>✕</Btn>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {err?.fields?.items && <div class="text-[11.5px] text-[#b42318]">{err.fields.items}</div>}
      <ErrorLine error={err?.error} />
      <div class="flex justify-between mt-3">
        {!locked ? <Btn onClick={() => setRows([...rows, blankRow(rows.length + 1)])}>Add question</Btn> : <span />}
        <Btn kind="primary" onClick={save}>Save and continue</Btn>
      </div>
    </Card>
  );
}

type CodeDraft = Omit<CodeRow, 'id'> & { id?: string };

function CodesStep({ ds, onSaved }: { ds: Dataset; onSaved: () => void }) {
  const locked = !!ds.assessment.marking_started_at;
  const [codes, setCodes] = useState<CodeDraft[]>(ds.misconceptions);
  const [err, setErr] = useState<ApiErr | null>(null);
  const original = new Map(ds.misconceptions.map((m) => [m.id, m]));
  const set = (i: number, patch: Partial<CodeDraft>) => setCodes(codes.map((c, j) => j === i ? { ...c, ...patch } : c));

  async function save() {
    setErr(null);
    try { await api('PUT', `/api/t/assessments/${ds.assessment.id}/misconceptions`, { codes }, ds.assessment.revision); onSaved(); }
    catch (x) { setErr(x as ApiErr); }
  }
  return (
    <Card title="Misconception codes" sub="A code can only be attached to an answer on a question it is linked to">
      <div class="flex flex-col gap-3">
        {codes.map((c, i) => {
          const old = c.id ? original.get(c.id) : undefined;
          return (
            <div key={c.id ?? `n${i}`} class="rounded-xl border border-[var(--color-border)] p-3 flex flex-col gap-2">
              <div class="grid sm:grid-cols-[100px_1fr_180px_auto] gap-2 items-end">
                <Field label="Code" error={err?.fields?.[`codes.${i}.code`]}><input class={inputCls} value={c.code} disabled={!!old && locked} onInput={(e) => set(i, { code: val(e).toUpperCase() })} placeholder="HT01" /></Field>
                <Field label="Meaning" error={err?.fields?.[`codes.${i}.meaning`]}><input class={inputCls} value={c.meaning} disabled={!!old && locked} onInput={(e) => set(i, { meaning: val(e) })} placeholder="Confuses conduction and convection" /></Field>
                <Field label="Short label"><input class={inputCls} value={c.short_label} disabled={!!old && locked} onInput={(e) => set(i, { short_label: val(e) })} /></Field>
                {!(old && locked) && <Btn kind="danger" onClick={() => setCodes(codes.filter((_, j) => j !== i))}>Remove</Btn>}
              </div>
              <div class="flex flex-wrap gap-1.5 text-[12px]">
                {ds.items.map((it) => {
                  const on = c.item_ids.includes(it.id);
                  const fixed = locked && !!old?.item_ids.includes(it.id);
                  return (
                    <label key={it.id} class={'px-2 py-1 rounded-lg border cursor-pointer ' + (on ? 'border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-text)]' : 'border-[var(--color-border)] text-[var(--color-text-muted)]')}>
                      <input type="checkbox" class="mr-1" checked={on} disabled={fixed}
                        onChange={() => set(i, { item_ids: on ? c.item_ids.filter((x) => x !== it.id) : [...c.item_ids, it.id] })} />
                      {it.label}
                    </label>
                  );
                })}
              </div>
              {err?.fields?.[`codes.${i}.item_ids`] && <div class="text-[11px] text-[#b42318]">{err.fields[`codes.${i}.item_ids`]}</div>}
            </div>
          );
        })}
      </div>
      {err?.fields?.codes && <div class="text-[11.5px] text-[#b42318]">{err.fields.codes}</div>}
      <ErrorLine error={err?.error} />
      <div class="flex justify-between mt-3">
        <Btn onClick={() => setCodes([...codes, { code: '', meaning: '', short_label: '', item_ids: [] }])}>Add code</Btn>
        <Btn kind="primary" onClick={save}>Save and go to marks</Btn>
      </div>
    </Card>
  );
}
